import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const OrderBook = ({ selectedPair }) => {
  const [orderBookData, setOrderBookData] = useState({ bids: [], asks: [] });
  const [precision, setPrecision] = useState(2);
  const [grouping, setGrouping] = useState('0.01');

  const groupingOptions = ['0.01', '0.1', '1', '10'];

  // Generate mock order book data
  useEffect(() => {
    const generateOrderBook = () => {
      const basePrice = 43250.75;
      const bids = [];
      const asks = [];

      // Generate bids (buy orders) - prices below current price
      for (let i = 0; i < 15; i++) {
        const price = basePrice - (i + 1) * Math.random() * 50;
        const size = Math.random() * 10;
        const total = price * size;
        bids.push({ price, size, total });
      }

      // Generate asks (sell orders) - prices above current price
      for (let i = 0; i < 15; i++) {
        const price = basePrice + (i + 1) * Math.random() * 50;
        const size = Math.random() * 10;
        const total = price * size;
        asks.push({ price, size, total });
      }

      return { bids, asks: asks.reverse() };
    };

    setOrderBookData(generateOrderBook());

    // Update order book every 2 seconds
    const interval = setInterval(() => {
      setOrderBookData(generateOrderBook());
    }, 2000);

    return () => clearInterval(interval);
  }, [selectedPair]);

  const formatPrice = (price) => {
    return price.toFixed(precision);
  };

  const formatSize = (size) => {
    return size.toFixed(4);
  };

  const formatTotal = (total) => {
    return total.toFixed(2);
  };

  const getMaxTotal = () => {
    const allTotals = [...orderBookData.bids, ...orderBookData.asks].map(order => order.total);
    return Math.max(...allTotals);
  };

  const OrderRow = ({ order, type, maxTotal }) => {
    const widthPercentage = (order.total / maxTotal) * 100;
    
    return (
      <div className="relative group cursor-pointer hover:bg-secondary-50 transition-colors duration-trading-fast">
        <div 
          className={`absolute inset-y-0 right-0 ${
            type === 'bid' ? 'bg-success-50' : 'bg-error-50'
          } transition-all duration-300`}
          style={{ width: `${widthPercentage}%` }}
        ></div>
        <div className="relative grid grid-cols-3 gap-2 px-3 py-1 text-xs font-data">
          <span className={`text-right ${type === 'bid' ? 'text-success' : 'text-error'}`}>
            {formatPrice(order.price)}
          </span>
          <span className="text-right text-text-primary">
            {formatSize(order.size)}
          </span>
          <span className="text-right text-text-secondary">
            {formatTotal(order.total)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-heading-semibold text-text-primary">
            Order Book
          </h3>
          <div className="flex items-center space-x-2">
            <select
              value={grouping}
              onChange={(e) => setGrouping(e.target.value)}
              className="text-xs border border-border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {groupingOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <button className="p-1 rounded hover:bg-secondary-50 transition-colors duration-trading-fast">
              <Icon name="Settings" size={14} className="text-text-secondary" />
            </button>
          </div>
        </div>

        {/* Column Headers */}
        <div className="grid grid-cols-3 gap-2 px-3 py-2 text-xs font-body-medium text-text-secondary border-b border-border">
          <span className="text-right">Price ({selectedPair ? selectedPair.split('/')[1] : ''})</span>
          <span className="text-right">Size ({selectedPair ? selectedPair.split('/')[0] : ''})</span>
          <span className="text-right">Total</span>
        </div>
      </div>

      {/* Order Book Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          {/* Asks (Sell Orders) */}
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-0">
              {orderBookData.asks.map((ask, index) => (
                <OrderRow 
                  key={`ask-${index}`}
                  order={ask}
                  type="ask"
                  maxTotal={getMaxTotal()}
                />
              ))}
            </div>
          </div>

          {/* Spread */}
          <div className="px-4 py-3 bg-secondary-50 border-y border-border">
            <div className="flex items-center justify-between">
              <div className="text-center">
                <div className="text-xs text-text-secondary mb-1">Spread</div>
                <div className="font-data text-sm text-text-primary">
                  {orderBookData.asks.length > 0 && orderBookData.bids.length > 0
                    ? (orderBookData.asks[orderBookData.asks.length - 1]?.price - orderBookData.bids[0]?.price).toFixed(2)
                    : '0.00'
                  }
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-text-secondary mb-1">Spread %</div>
                <div className="font-data text-sm text-text-primary">
                  {orderBookData.asks.length > 0 && orderBookData.bids.length > 0
                    ? (((orderBookData.asks[orderBookData.asks.length - 1]?.price - orderBookData.bids[0]?.price) / orderBookData.bids[0]?.price) * 100).toFixed(3)
                    : '0.000'
                  }%
                </div>
              </div>
            </div>
          </div>

          {/* Bids (Buy Orders) */}
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-0">
              {orderBookData.bids.map((bid, index) => (
                <OrderRow 
                  key={`bid-${index}`}
                  order={bid}
                  type="bid"
                  maxTotal={getMaxTotal()}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer Stats */}
      <div className="p-4 border-t border-border bg-secondary-50">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <div className="text-xs text-text-secondary mb-1">Bid Volume</div>
            <div className="font-data text-sm text-success">
              {orderBookData.bids.reduce((sum, bid) => sum + bid.size, 0).toFixed(2)}
            </div>
          </div>
          <div>
            <div className="text-xs text-text-secondary mb-1">Ask Volume</div>
            <div className="font-data text-sm text-error">
              {orderBookData.asks.reduce((sum, ask) => sum + ask.size, 0).toFixed(2)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderBook;